import { Builder } from '@builder.io/react';
import React from 'react';

import { ProgressiveImage } from '@videoblocks/storywind';

import getAssetSource from '../../../app/Search/utils/getAssetSource';
import { Button } from '../../storyblocks-design-system/components/atoms/Button/Button';

const CircleImage = ({ src, alt, className = '' }) =>
  src ? (
    <div
      className={`bg-gray-100 w-32 h-32 absolute rounded-full overflow-hidden ${className} border-4 border-white`}
    >
      <ProgressiveImage
        src={getAssetSource(src)}
        alt={alt}
        className="w-full h-full object-cover"
      />
    </div>
  ) : null;

const ExploreMediaLibrary = ({
  shouldOpenSideMenu,
  title = '100% exclusive music tracks from award-winning artists',
  buttonText = 'Explore Music Library',
  buttonHref = '/music',
  description = 'Create content that stands out with unlimited access to 33,000+ royalty free music tracks including our Storyblocks Label collection.',
  mainImage = '/explore-music/logo.png',
  mainImageAlt = '',
  firstImage = '/explore-music/image-1.png',
  firstImageAlt = '',
  secondImage = '/explore-music/image-2.png',
  secondImageAlt = '',
  thirdImage = '/explore-music/image-3.png',
  thirdImageAlt = '',
}) => (
  <div
    className={`flex flex-col lg:flex-row w-full max-w-5xl mx-auto my-16 px-6 md:px-12 lg:px-24 gap-y-12 relative ${
      shouldOpenSideMenu ? 'lg:gap-x-8' : 'lg:gap-x-8 2xl:gap-x-138'
    }`}
  >
    <div className="my-2 lg:flex-1">
      <div className="w-full h-120 flex justify-center items-center">
        <div className="w-350px h-350px bg-gray-900 rounded-3xl relative">
          <CircleImage
            src={firstImage}
            alt={firstImageAlt}
            className="-right-16 -top-16"
          />
          <CircleImage
            src={secondImage}
            alt={secondImageAlt}
            className="-bottom-16 -right-8"
          />
          <CircleImage
            src={thirdImage}
            alt={thirdImageAlt}
            className="bottom-8 -left-16"
          />
          <ProgressiveImage
            src={getAssetSource(mainImage)}
            alt={mainImageAlt}
            className="absolute w-2/3 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-2/3"
          />
        </div>
      </div>
    </div>
    <div className="my-2 lg:flex-1">
      <div className="w-full h-120 flex justify-center items-center">
        <div className="h-350px flex flex-col justify-center items-start">
          <p className="text-3xl font-bold">{title}</p>
          <p className="text-base">{description}</p>
          <Button
            href={buttonHref}
            variant="secondary"
            size="large"
            className="mt-4"
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </div>
  </div>
);

export default ExploreMediaLibrary;

Builder.registerComponent(ExploreMediaLibrary, {
  name: 'Explore Media Library',
  inputs: [
    {
      name: 'shouldOpenSideMenu',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'mainImage',
      type: 'string',
      defaultValue: '/explore-music/logo.png',
      advanced: true,
      helperText: 'Image URL for the main image in the block',
    },
    {
      name: 'mainImageAlt',
      type: 'string',
      defaultValue: '',
      advanced: true,
      helperText:
        'Alt text for the main image. Leave empty if the image is purely decorative so screen readers skip it.',
    },
    {
      name: 'firstImage',
      type: 'string',
      defaultValue: '/explore-music/image-1.png',
      advanced: true,
      helperText: 'Image URL for the first circle image (top right).',
    },
    {
      name: 'firstImageAlt',
      type: 'string',
      defaultValue: '',
      advanced: true,
      helperText:
        'Alt text for the first circle image. Leave empty if decorative.',
    },
    {
      name: 'secondImage',
      type: 'string',
      defaultValue: '/explore-music/image-2.png',
      advanced: true,
      helperText: 'Image URL for the second circle image (bottom right).',
    },
    {
      name: 'secondImageAlt',
      type: 'string',
      defaultValue: '',
      advanced: true,
      helperText:
        'Alt text for the second circle image. Leave empty if decorative.',
    },
    {
      name: 'thirdImage',
      type: 'string',
      defaultValue: '/explore-music/image-3.png',
      advanced: true,
      helperText: 'Image URL for the third circle image (bottom left).',
    },
    {
      name: 'thirdImageAlt',
      type: 'string',
      defaultValue: '',
      advanced: true,
      helperText:
        'Alt text for the third circle image. Leave empty if decorative.',
    },
    {
      name: 'title',
      type: 'string',
      defaultValue: '100% exclusive music tracks from award-winning artists',
    },
    {
      name: 'description',
      type: 'string',
      defaultValue:
        'Create content that stands out with unlimited access to 33,000+ royalty free music tracks including our Storyblocks Label collection.',
    },
    {
      name: 'buttonText',
      type: 'string',
      defaultValue: 'Explore Music Library',
    },
    {
      name: 'buttonHref',
      type: 'string',
      defaultValue: '/music',
    },
  ],
});
